import { makeAutoObservable, runInAction } from 'mobx';
import { AxiosInstance } from 'axios';
import { AxiosResponseHelper } from 'utilities/AxiosResponseHelper';
import { INotificationsStore } from './NotificationsStore';

/** Response from /api/SasToken endpoint */
export interface SasTokenResponse {
  token: string;
  expiresAt: string | null;
  endpoint: string;
  entityPath: string;
}

/** Cache entry with fetch timestamp */
interface CachedToken {
  token: SasTokenResponse;
  fetchedAt: Date;
}

export interface ISasTokenStore {
  token: SasTokenResponse | null;
  isLoading: boolean;
  loadError: string | null;
  lastFetchedOrgId: string | null;

  /** Preload token for the current organization */
  LoadToken(apiInstance: AxiosInstance, organizationId: string): Promise<void>;

  /** Get the raw token string if available */
  getTokenString(): string | null;

  /** Check if token is valid (not expired, not expiring soon, not exceeding 15-minute cache limit) */
  isTokenValid(): boolean;

  /** Invalidate cached token to force a refresh */
  InvalidateToken(): void;
}

/** 
 * Manages SAS tokens for video blob access with per-organization caching.
 * Automatically detects org switches and fetches new tokens as needed.
 * Tokens are never cached for longer than 15 minutes from fetch time.
 */
export class SasTokenStore implements ISasTokenStore {
  token: SasTokenResponse | null = null;
  isLoading: boolean = false;
  loadError: string | null = null;
  lastFetchedOrgId: string | null = null;

  private _notificationStore: INotificationsStore;
  /** Refresh buffer (minutes) — matches backend default of 5 minutes */
  private readonly REFRESH_BUFFER_MINUTES = 5;
  /** Maximum cache duration: 15 minutes from fetch time */
  private readonly MAX_CACHE_DURATION_MINUTES = 15;
  /** Timestamp when token was last fetched */
  private _tokenFetchedAt: Date | null = null;

  constructor(notificationStore: INotificationsStore) {
    this._notificationStore = notificationStore;
    makeAutoObservable(this);
  }

  async LoadToken(apiInstance: AxiosInstance, organizationId: string): Promise<void> {
    // Check if we need to fetch a fresh token
    if (this.token && !this.isTokenExpiringSoon() && this.lastFetchedOrgId === organizationId) {
      // Token is still valid for this org, skip fetch
      return;
    }

    // Org switched or token expired, fetch new one
    if (this.lastFetchedOrgId !== organizationId) {
      this.InvalidateToken();
    }

    runInAction(() => {
      this.isLoading = true;
      this.loadError = null;
    });

    try {
      const response = await apiInstance.get<SasTokenResponse>('api/SasToken');

      if (AxiosResponseHelper.IsSuccessfulResponse(response) && response.data) {
        const now = new Date();
        runInAction(() => {
          this.token = response.data as SasTokenResponse;
          this._tokenFetchedAt = now;
          this.lastFetchedOrgId = organizationId;
        });
      } else {
        runInAction(() => {
          this.loadError = 'Failed to load SAS token';
        });
      }
    } catch (error) {
      runInAction(() => {
        this.loadError = 'Failed to load SAS token: network error';
      });
      console.error('SasTokenStore: Failed to load token', error);
    } finally {
      runInAction(() => {
        this.isLoading = false;
      });
    }
  }

  getTokenString(): string | null {
    return this.token?.token ?? null;
  }

  isTokenValid(): boolean {
    if (!this.token?.expiresAt) {
      return !!this.token; // If no expiry, assume valid
    }
    return !this.isTokenExpiringSoon();
  }

  InvalidateToken(): void {
    this.token = null;
    this._tokenFetchedAt = null;
    this.lastFetchedOrgId = null;
  }

  /**
   * Checks if token is expiring within the refresh buffer window or exceeding max cache duration.
   * Returns true if expired, will expire within REFRESH_BUFFER_MINUTES, 
   * or has been cached for longer than MAX_CACHE_DURATION_MINUTES.
   */
  private isTokenExpiringSoon(): boolean {
    if (!this.token || !this._tokenFetchedAt) {
      return true; // No token or fetch time, needs refresh
    }

    const now = new Date();
    const timeSinceFetch = now.getTime() - this._tokenFetchedAt.getTime();

    // Check if cache age exceeds max duration
    const maxCacheDurationMs = this.MAX_CACHE_DURATION_MINUTES * 60 * 1000;
    if (timeSinceFetch >= maxCacheDurationMs) {
      console.debug(`SasTokenStore: Token exceeded max cache duration (${this.MAX_CACHE_DURATION_MINUTES}m)`);
      return true;
    }

    if (!this.token.expiresAt) {
      return false; // No expiration, assume it's good
    }

    const expiresAt = new Date(this.token.expiresAt);
    const timeUntilExpiration = expiresAt.getTime() - now.getTime();
    const bufferMs = this.REFRESH_BUFFER_MINUTES * 60 * 1000;

    return timeUntilExpiration <= bufferMs;
  }
}
